import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Save, Stethoscope, Briefcase, ArrowLeft, Calendar, Clock } from 'lucide-react';
import { getProfessionals, createProfessional, updateProfessional, deleteProfessional } from '../services/professionalApi';
import { getProfessionalAvailability, createProfessionalAvailability, deleteProfessionalAvailability } from '../services/api';

function AvailabilityModal({ professional, onClose }) {
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: '', start_time: '', end_time: '' });

  useEffect(() => {
    loadAvailability();
  }, []);

  const loadAvailability = async () => {
    setLoading(true);
    try {
      const res = await getProfessionalAvailability(professional.id);
      setAvailability(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.date || !form.start_time || !form.end_time) {
      alert('Preencha data, horário de início e fim');
      return;
    }
    setSaving(true);
    try {
      await createProfessionalAvailability(professional.id, form);
      setForm({ date: '', start_time: '', end_time: '' });
      loadAvailability();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteProfessionalAvailability(id);
      loadAvailability();
    } catch (err) {
      alert('Erro ao remover: ' + (err.response?.data?.error || err.message));
    }
  };

  const formatDate = (dateStr) => {
    const [year, month, day] = String(dateStr).slice(0, 10).split('-').map(Number);
    const d = new Date(year, month - 1, day); // construído em hora local, sem ambiguidade de fuso
    return d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
              <Calendar size={20} className="text-purple-600" />
              Disponibilidade
            </h3>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{professional.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleAdd} className="bg-gray-50 dark:bg-slate-900 rounded-xl p-4 mb-4 space-y-3">
          <p className="text-xs font-bold text-gray-600 dark:text-slate-400 uppercase">Adicionar dia disponível</p>
          <div>
            <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Data</label>
            <input
              type="date"
              value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              min={new Date().toISOString().slice(0, 10)}
              className="w-full p-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Início</label>
              <input
                type="time"
                value={form.start_time}
                onChange={e => setForm({ ...form, start_time: e.target.value })}
                className="w-full p-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Fim</label>
              <input
                type="time"
                value={form.end_time}
                onChange={e => setForm({ ...form, end_time: e.target.value })}
                className="w-full p-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 disabled:opacity-50"
          >
            <Plus size={16} />
            {saving ? 'Adicionando...' : 'Adicionar'}
          </button>
        </form>

        <p className="text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-2">Dias já cadastrados</p>
        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-4">Carregando...</p>
          ) : availability.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">Nenhuma disponibilidade cadastrada ainda.</p>
          ) : (
            availability.map(a => (
              <div key={a.id} className="flex items-center justify-between bg-gray-50 dark:bg-slate-700 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-slate-200 capitalize">
                    {formatDate(a.date)} — {a.start_time.slice(0, 5)} às {a.end_time.slice(0, 5)}
                  </span>
                </div>
                <button onClick={() => handleDelete(a.id)} className="text-red-400 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ProfessionalConfig() {
  const [professionals, setProfessionals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [availabilityFor, setAvailabilityFor] = useState(null);
  const [form, setForm] = useState({
    name: '',
    specialty: '',
    crm: '',
    value: '',
    color: '#4f46e5'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadProfessionals();
  }, []);

  const loadProfessionals = async () => {
    try {
      const res = await getProfessionals();
      setProfessionals(res.data);
    } catch (err) {
      console.error('Erro ao carregar profissionais:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await updateProfessional(editing.id, form);
      } else {
        await createProfessional(form);
      }
      await loadProfessionals();
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', specialty: '', crm: '', value: '', color: '#4f46e5' });
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (prof) => {
    setEditing(prof);
    setForm({
      name: prof.name,
      specialty: prof.specialty || '',
      crm: prof.crm || '',
      value: prof.value || '',
      color: prof.color || '#4f46e5'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este profissional?')) return;
    try {
      await deleteProfessional(id);
      await loadProfessionals();
    } catch (err) {
      alert('Erro ao remover: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
    setForm({ name: '', specialty: '', crm: '', value: '', color: '#4f46e5' });
  };

  return (
  <div className="p-6 max-w-7xl mx-auto">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => window.location.href = '/appointments'}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-300 transition-colors"
        >
          <ArrowLeft size={16} />
          Voltar
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Stethoscope size={28} className="text-purple-600" />
            Profissionais
          </h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">
            Cadastre médicos, advogados ou outros profissionais
          </p>
        </div>
      </div>
      <button
        onClick={() => setShowForm(true)}
        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-bold hover:bg-purple-700"
      >
        <Plus size={16} /> Novo Profissional
      </button>
    </div>

      {showForm && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 mb-4">
            {editing ? 'Editar Profissional' : 'Novo Profissional'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-1">
                Nome Completo *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="Dr. Nome Sobrenome"
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm outline-none focus:border-purple-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-1">
                Especialidade
              </label>
              <input
                type="text"
                value={form.specialty}
                onChange={(e) => setForm({...form, specialty: e.target.value})}
                placeholder="Ex: Cardiologia, Clínica Geral"
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm outline-none focus:border-purple-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-1">
                CRM / Registro
              </label>
              <input
                type="text"
                value={form.crm}
                onChange={(e) => setForm({...form, crm: e.target.value})}
                placeholder="CRM 00000"
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm outline-none focus:border-purple-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-1">
                Valor da Consulta
              </label>
              <input
                type="text"
                value={form.value}
                onChange={(e) => setForm({...form, value: e.target.value})}
                placeholder="R$ 0,00"
                className="w-full p-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm outline-none focus:border-purple-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-gray-600 dark:text-slate-400 uppercase mb-1">
                Cor na Agenda
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({...form, color: e.target.value})}
                  className="w-16 h-10 rounded-lg border border-gray-200 dark:border-slate-600 cursor-pointer"
                />
                <span className="text-sm text-gray-600 dark:text-slate-400">{form.color}</span>
              </div>
            </div>
            <div className="md:col-span-2 flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-5 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-slate-600"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-xl text-sm font-bold hover:bg-purple-700 disabled:opacity-50"
              >
                <Save size={16} />
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid gap-3">
        {professionals.map(prof => (
          <div
            key={prof.id}
            className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
            style={{ borderLeft: `4px solid ${prof.color}` }}
          >
            <div
              className="w-12 h-12 rounded-full text-white flex items-center justify-center font-bold text-lg flex-shrink-0"
              style={{ background: prof.color }}
            >
              {prof.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="font-bold text-gray-900 dark:text-slate-100">{prof.name}</div>
              <div className="text-sm text-gray-600 dark:text-slate-400">
                {prof.specialty || 'Sem especialidade'}
                {prof.crm && ` · ${prof.crm}`}
                {prof.value && ` · ${prof.value}`}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setAvailabilityFor(prof)}
                className="flex items-center gap-1.5 px-3 py-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg text-xs font-bold"
              >
                <Calendar size={14} />
                Disponibilidade
              </button>
              <button
                onClick={() => handleEdit(prof)}
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={() => handleDelete(prof.id)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {professionals.length === 0 && (
          <div className="text-center py-12 text-gray-400 dark:text-slate-500">
            <Stethoscope size={48} className="mx-auto mb-3 opacity-30" />
            <p>Nenhum profissional cadastrado</p>
            <p className="text-sm mt-1">Clique em "Novo Profissional" para começar</p>
          </div>
        )}
      </div>

      {availabilityFor && (
        <AvailabilityModal
          professional={availabilityFor}
          onClose={() => setAvailabilityFor(null)}
        />
      )}
    </div>
  );
}

export default ProfessionalConfig;
